import dayjs from 'dayjs'
import { fetchTaskCount, fetchTasks } from '../shared/wealthboxQueryHelpers'
import WealthboxTaskPanel from './WealthboxTaskPanel'

// Preset shape mirrors `redtailMetric/presets.js`:
//   - fetchData: returns either a numeric count (metric tile) or an array of
//     items (when the panel is open and we need the full list)
//   - compute: passthrough - all filtering happens server-side via params we
//     hand to Wealthbox `/v1/tasks`
//   - DetailPanel: opens on metric click
//   - usesField: list of admin-form fields the preset consumes (drives
//     conditional visibility in ConfigEditor)
//
// IMPORTANT - WHY THERE IS NO "PAST DUE" OR "DUE WITHIN N DAYS" PRESET:
// The Wealthbox public API does NOT support filtering or sorting `/v1/tasks`
// by `due_date`. Documented filters are only:
//   /v1/tasks{?resource_id}{?resource_type}{?assigned_to}{?assigned_to_team}
//            {?created_by}{?completed}{?task_type}{?subtasks}
//            {?updated_since}{?updated_before}
// (Verified against https://dev.wealthbox.com, Pipedream's Wealthbox source,
// and the third-party wealthbox-cli which explicitly notes the API lacks
// server-side filters for many fields. Wealthbox's `events` endpoint DOES
// expose `start_date_min/_max` + `order` for events; for tasks it does not.)
//
// Earlier versions of this file passed `due_date[lte]` / `due_date[gte]` --
// those were silently ignored, which is why "Past Due Tasks" and "Due Within
// 7 Days" returned the same number (the total open task count). See
// `.plans/SMT-1352.md` for the full investigation and the proposed follow-up
// if product wants Past Due / Due Within for real.

function buildFilterParams (config) {
  const params = {}
  if (config?.includeCompleted) {
    // `completed=true` is additive per Wealthbox docs ("Return completed
    // tasks as well"). Default behavior already excludes completed.
    params.completed = true
  }
  if (config?.taskType) {
    // Wealthbox enum: `all` | `parents` | `subtasks`. Default is `all`.
    params.task_type = config.taskType
  }
  if (config?.teamId != null && config.teamId !== '') {
    // `assigned_to_team` is a standalone documented filter. Coerce to int so
    // the admin can type a string in the number input without breaking.
    const teamId = Number(config.teamId)
    if (Number.isFinite(teamId)) {
      params.assigned_to_team = teamId
    }
  }
  // `resource_type` is intentionally not derived from config: Wealthbox treats
  // it as paired with `resource_id` and ignores it standalone. In Client scope
  // the pair is set in `buildTaskParams`; in My Book scope neither is set.
  return params
}

// Top-level fields on the listTasks request that the core-data server
// translates via the user's resolved token (assigned_to / created_by). Kept
// here so callers don't need to know the Wealthbox externalUserId.
function buildScopeFields (config) {
  const fields = {}
  if (config?.scope) fields.scope = config.scope
  if (config?.createdByScope) fields.createdByScope = config.createdByScope
  return fields
}

const openTasks = {
  key: 'open-tasks',
  displayName: 'Open Tasks',
  usesFields: [],
  defaults: {
    icon: 'wealthboxSmall',
    title: 'Open Tasks',
    label: 'Incomplete',
    format: '0,0'
  },

  async fetchData ({ scope, contactId, clientId, fullList }, config) {
    const params = buildFilterParams(config)
    const scopeFields = buildScopeFields(config)
    if (fullList) {
      const { tasks } = await fetchTasks({ scope, contactId, clientId, params, scopeFields })
      return tasks
    }
    return fetchTaskCount({ scope, contactId, clientId, params, scopeFields })
  },

  compute (data) {
    return data
  },

  DetailPanel: WealthboxTaskPanel
}

const recentlyUpdatedTasks = {
  key: 'recently-updated-tasks',
  displayName: 'Recently Updated Tasks',
  usesFields: ['updatedWithinDays'],
  defaults: {
    icon: 'wealthboxSmall',
    title: 'Recent Activity',
    label: 'Updated This Week',
    format: '0,0'
  },

  async fetchData ({ scope, contactId, clientId, fullList }, config) {
    const updatedWithinDays = config?.updatedWithinDays ?? 7
    const params = {
      ...buildFilterParams(config),
      updated_since: dayjs().subtract(updatedWithinDays, 'day').toISOString()
    }
    const scopeFields = buildScopeFields(config)
    if (fullList) {
      const { tasks } = await fetchTasks({ scope, contactId, clientId, params, scopeFields })
      return tasks
    }
    return fetchTaskCount({ scope, contactId, clientId, params, scopeFields })
  },

  compute (data) {
    return data
  },

  DetailPanel: WealthboxTaskPanel
}

const staleTasks = {
  key: 'stale-tasks',
  displayName: 'Stale Tasks',
  usesFields: ['staleAfterDays'],
  defaults: {
    icon: 'wealthboxSmall',
    title: 'Stale Tasks',
    label: 'Untouched',
    format: '0,0'
  },

  async fetchData ({ scope, contactId, clientId, fullList }, config) {
    const staleAfterDays = config?.staleAfterDays ?? 30
    const params = {
      ...buildFilterParams(config),
      updated_before: dayjs().subtract(staleAfterDays, 'day').toISOString()
    }
    const scopeFields = buildScopeFields(config)
    if (fullList) {
      const { tasks } = await fetchTasks({ scope, contactId, clientId, params, scopeFields })
      return tasks
    }
    return fetchTaskCount({ scope, contactId, clientId, params, scopeFields })
  },

  compute (data) {
    return data
  },

  DetailPanel: WealthboxTaskPanel
}

const presets = {
  'open-tasks': openTasks,
  'recently-updated-tasks': recentlyUpdatedTasks,
  'stale-tasks': staleTasks
}

export default presets
